import { AlertTriangle, GitMerge } from "lucide-react";

/**
 * Blocking modal shown when the backend's /dedupe/check returns matches.
 *
 * Props:
 *   matches: { contacts: [], leads: [], deals: [] } as returned by the API
 *   entityLabel: string label of the thing about to be created ("Contact", "Lead", "Deal")
 *   onContinue: () => void — user clicked "Create anyway"
 *   onCancel: () => void
 *   onOpenExisting?: (row, kind) => void — optional; when present, matches are clickable
 *   onMergeInto?: (row, kind) => void  — optional; when present, each row shows a "Merge into"
 *                                        button. Handler is responsible for applying the
 *                                        pending form data to the picked target and closing.
 */
export default function DuplicateWarningModal({ matches, entityLabel, onContinue, onCancel, onOpenExisting, onMergeInto }) {
  const groups = [
    { key: "contacts", label: "Contact", rows: matches.contacts || [] },
    { key: "leads",    label: "Lead",    rows: matches.leads || [] },
    { key: "deals",    label: "Deal",    rows: matches.deals || [] },
  ].filter((g) => g.rows.length > 0);

  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <>
      <div className="side-panel-backdrop" onClick={onCancel} />
      <div className="side-panel" data-variant="center" data-testid="dedupe-warning-modal">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <div className="flex items-start gap-3">
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: "rgba(234,67,53,0.10)", color: "#EA4335",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <AlertTriangle size={20} />
            </div>
            <div>
              <h2 className="text-xl">Possible duplicate detected</h2>
              <div className="mono text-[11px] text-mute mt-1">
                {total === 1 ? "1 existing record matches" : `${total} existing records match`}. Review before creating a new {entityLabel.toLowerCase()}.
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="mono text-[10px] uppercase text-mute tracking-wider mb-2">
                {g.label}{g.rows.length === 1 ? "" : "s"}
              </div>
              <div className="space-y-2">
                {g.rows.map((row) => {
                  const clickable = !!onOpenExisting;
                  return (
                    <div
                      key={row.id}
                      style={{
                        background: "var(--pg-surface-2)",
                        border: "1px solid var(--pg-border)",
                        borderRadius: 8,
                        padding: "10px 12px",
                        display: "flex", alignItems: "center", gap: 10,
                      }}
                      data-testid={`dedupe-match-${g.key}-${row.id}`}
                    >
                      <button
                        type="button"
                        disabled={!clickable}
                        onClick={clickable ? () => onOpenExisting(row, g.key) : undefined}
                        className="flex-1 text-left"
                        style={{
                          background: "transparent", border: "none", padding: 0,
                          cursor: clickable ? "pointer" : "default",
                        }}
                        data-testid={`dedupe-open-${g.key}-${row.id}`}
                      >
                        <div className="text-sm" style={{ color: "var(--pg-text)" }}>
                          {row.name || "(no name)"}
                          {g.key === "deals" && row.status ? (
                            <span className="mono text-[10px] uppercase ml-2" style={{ color: "var(--pg-text-mute)" }}>
                              · {row.status}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--pg-text-mute)" }}>
                          {[row.email, row.phone, row.company].filter(Boolean).join(" · ")}
                          {g.key === "deals" && row.value != null ? ` · $${row.value.toLocaleString()}` : ""}
                        </div>
                      </button>
                      {onMergeInto && g.key !== "deals" && (
                        <button
                          type="button"
                          onClick={() => onMergeInto(row, g.key)}
                          className="pg-btn"
                          title={`Merge the info you just typed into ${row.name}`}
                          data-testid={`dedupe-merge-${g.key}-${row.id}`}
                          style={{ flexShrink: 0 }}
                        >
                          <GitMerge size={13} /> Merge into
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onCancel} data-testid="dedupe-cancel">
            Cancel &amp; review
          </button>
          <button
            type="button"
            className="pg-btn pg-btn-primary"
            onClick={onContinue}
            data-testid="dedupe-continue"
          >
            Create anyway
          </button>
        </div>
      </div>
    </>
  );
}
